<template>
  <aside class="batch-live" :class="{'is-empty': guides.length === 0, 'is-open': open}">
    <template v-if="guides.length === 0">
      <div class="batch-live-empty">
        <div class="batch-live-empty-icon">
          <fa-icon :icon="['fal', 'layer-group']" size="lg"/>
        </div>
        <div class="batch-live-empty-title">{{ emptyTitle }}</div>
        <ul class="batch-live-tips" v-if="adding">
          <li><fa-icon :icon="['fal', 'check']"/>{{ typeTip }}</li>
          <li><fa-icon :icon="['fal', 'check']"/>Restam {{ limit }} vagas neste lote</li>
          <li><fa-icon :icon="['fal', 'check']"/>Título e repasses são recalculados na hora</li>
        </ul>
        <ul class="batch-live-tips" v-else>
          <li><fa-icon :icon="['fal', 'check']"/>{{ typeTip }}</li>
          <li><fa-icon :icon="['fal', 'check']"/>Até {{ limit }} guias por lote neste plano</li>
          <li><fa-icon :icon="['fal', 'check']"/>Título e repasses nascem no fechamento</li>
        </ul>
      </div>
    </template>
    <template v-else>
      <div class="batch-live-head" @click="open = !open">
        <div class="batch-live-eyebrow">Novo lote · {{ guideTypeName(type) }}</div>
        <h3 class="batch-live-title">{{ planName }}</h3>
        <div class="batch-live-count">
          <span>
            <b>{{ guides.length }}</b>
            {{ guides.length === 1 ? 'guia selecionada' : 'guias selecionadas' }}
            <span class="batch-live-total-sm">· {{ total | currency }}</span>
          </span>
          <span class="pill" :class="{'pill-pending': overLimit}">
            {{ guides.length }} / {{ limit }}
          </span>
        </div>
        <div class="batch-live-bar" :class="{warn: nearLimit}">
          <i :style="{width: `${Math.min(100, Math.max(2, progress))}%`}"></i>
        </div>
      </div>
      <div class="batch-live-body">
        <div class="batch-live-sec">
          <div class="batch-live-row big">
            <span>Total faturado</span>
            <span class="v num">{{ total | currency }}</span>
          </div>
          <div class="batch-live-row">
            <span class="text-gray">Itens</span>
            <span class="v num">{{ items }}</span>
          </div>
        </div>
        <div class="batch-live-sec">
          <div class="batch-live-sec-title">Por profissional</div>
          <div class="batch-live-row" v-for="group in visibleProfessionals" :key="group.name">
            <span class="who">
              {{ group.name }}
              <small>{{ group.guides === 1 ? '1 guia' : `${group.guides} guias` }}</small>
            </span>
            <span class="v num">{{ group.amount | currency }}</span>
          </div>
          <a href="#" class="batch-live-more" v-if="hiddenProfessionals > 0"
             @click.prevent="showAllProfessionals = true">
            e mais {{ hiddenProfessionals }}
          </a>
        </div>
        <div class="batch-live-sec">
          <div class="batch-live-sec-title">Previsão de recebimento</div>
          <div class="batch-live-row" v-if="!editingDate">
            <span class="v" :class="{'text-gray': !displayDate}">
              {{ displayDate ? dateFilter(displayDate) : '—' }}
              <a href="#" class="batch-live-edit" @click.prevent="startEditing">
                {{ displayDate ? 'editar' : 'informar' }}
              </a>
            </span>
            <span class="text-gray"><small>{{ hint }}</small></span>
          </div>
          <div class="batch-live-date form-group" :class="{'has-error': $v.localDate.$error}"
               v-else>
            <dx-input-date class="form-input input-sm" placeholder="00/00/0000"
                           v-model="localDate" @blur="$v.localDate.$touch()"/>
            <button class="btn btn-sm btn-primary btn-action btn-icon" @click="saveDate">
              <fa-icon :icon="['fal', 'check']"/>
            </button>
            <button class="btn btn-sm btn-gray btn-action btn-icon" @click="cancelDate">
              <fa-icon :icon="['fal', 'times']"/>
            </button>
            <div class="form-input-hint" v-if="$v.localDate.$error">Informe uma data futura</div>
          </div>
        </div>
        <div class="batch-live-note" v-if="overLimit">
          <fa-icon :icon="['fal', 'exclamation-triangle']"/>
          <span>
            Limite de {{ limit }} guias por lote atingido.
            As próximas serão distribuídas em lotes seguintes.
            <label class="form-switch batch-live-switch">
              <input type="checkbox" :checked="autoSplit"
                     @change="$emit('update:autoSplit', $event.target.checked)">
              <i class="form-icon"></i>Criar o próximo lote automaticamente
            </label>
            <span class="batch-live-split" v-if="autoSplit">
              {{ batchCount }} lotes · {{ splitLabel }}
            </span>
          </span>
        </div>
        <div class="batch-live-note" v-for="warning in warnings" :key="warning">
          <fa-icon :icon="['fal', 'exclamation-triangle']"/><span>{{ warning }}</span>
        </div>
        <div class="batch-live-foot">
          <button class="btn btn-primary btn-block btn-icon btn-icon-left"
                  :class="[{loading: building}, buildTooltip ? 'tooltip' : '']"
                  :disabled="building || !canBuild"
                  :data-tooltip="buildTooltip"
                  @click="$emit('build')">
            <fa-icon :icon="['fal', 'layer-group']"/>{{ buildLabel }}
          </button>
          <small>{{ footHint }}</small>
          <button class="btn btn-link btn-sm btn-block" @click="$emit('clear')">
            Limpar seleção
          </button>
        </div>
      </div>
    </template>
  </aside>
</template>

<script>
import moment from 'moment';
import { required } from 'vuelidate/lib/validators';
import { date, minDate } from '@/data/validators';
import dateFilter from '@/filters/date';
import {
  guideTypeName, groupByProfessional, sumBy, forecastHint, passwordExpiringDays,
  PASSWORD_EXPIRING_DAYS,
} from '../service';

export default {
  props: {
    guides: {
      type: Array,
      default: () => [],
    },
    limit: {
      type: Number,
      default: 100,
    },
    forecast: {
      type: Object,
      default: null,
    },
    rule: {
      type: Object,
      default: null,
    },
    allowMixed: {
      type: Boolean,
      default: false,
    },
    planName: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: null,
    },
    canCreate: {
      type: Boolean,
      default: true,
    },
    adding: {
      type: Boolean,
      default: false,
    },
    autoSplit: {
      type: Boolean,
      default: true,
    },
    expectedPaymentAt: {
      type: String,
      default: null,
    },
    building: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      open: false,
      editingDate: false,
      localDate: this.expectedPaymentAt,
      showAllProfessionals: false,
    };
  },
  computed: {
    forecastDate() {
      return this.forecast && this.forecast.date
        ? moment(this.forecast.date).format('YYYY-MM-DD')
        : null;
    },
    displayDate() {
      return this.expectedPaymentAt || this.forecastDate;
    },
    hint() {
      if (this.expectedPaymentAt) return 'informada manualmente';
      return forecastHint(this.rule, this.forecast);
    },
    typeTip() {
      if (this.adding) {
        return this.allowMixed ? 'Guias de consulta e SP/SADT do mesmo plano' : 'Só guias do mesmo tipo do lote';
      }
      return this.allowMixed ? 'Consulta e SP/SADT podem ir no mesmo lote' : 'Um tipo de guia por lote';
    },
    emptyTitle() {
      return this.adding
        ? 'Marque as guias que entram neste lote'
        : 'Marque guias na lista para montar um lote';
    },
    total() {
      return sumBy(this.guides, guide => guide.totals && guide.totals.informed);
    },
    items() {
      return this.guides.reduce((sum, guide) => sum + (guide.itemsCount || 0), 0);
    },
    professionals() {
      return groupByProfessional(this.guides);
    },
    visibleProfessionals() {
      return this.showAllProfessionals ? this.professionals : this.professionals.slice(0, 5);
    },
    hiddenProfessionals() {
      return this.professionals.length - this.visibleProfessionals.length;
    },
    progress() {
      return (this.guides.length / this.limit) * 100;
    },
    nearLimit() {
      return this.progress >= 90;
    },
    overLimit() {
      return this.guides.length > this.limit;
    },
    batchCount() {
      return Math.ceil(this.guides.length / this.limit);
    },
    splitLabel() {
      const rest = this.guides.length % this.limit || this.limit;
      const full = Array.from({ length: this.batchCount - 1 }, () => this.limit);
      return `${[...full, rest].join(' + ')} guias`;
    },
    warnings() {
      const expiring = this.guides.filter(guide => passwordExpiringDays(guide) !== null);
      if (expiring.length === 0) return [];
      const count = expiring.length === 1 ? 'Uma guia' : `${expiring.length} guias`;
      const days = PASSWORD_EXPIRING_DAYS;
      return [`${count} com senha de autorização vencendo em até ${days} dias.`];
    },
    canBuild() {
      return this.canCreate && (!this.overLimit || this.autoSplit);
    },
    buildLabel() {
      return this.overLimit && this.autoSplit ? `Montar ${this.batchCount} lotes` : 'Montar lote';
    },
    footHint() {
      return this.canCreate
        ? 'Cria o título a receber e os repasses por profissional'
        : this.buildTooltip;
    },
    buildTooltip() {
      if (!this.canCreate) return 'Sem permissão para criar lotes';
      if (this.overLimit && !this.autoSplit) {
        return `Reduza para ${this.limit} guias ou ligue a criação automática`;
      }
      return null;
    },
  },
  validations() {
    return {
      localDate: {
        required,
        date,
        minDate: minDate(moment().format('YYYY-MM-DD')),
      },
    };
  },
  watch: {
    expectedPaymentAt(value) {
      this.localDate = value;
    },
  },
  methods: {
    guideTypeName,
    dateFilter,
    startEditing() {
      this.localDate = this.displayDate;
      this.$v.localDate.$reset();
      this.editingDate = true;
    },
    saveDate() {
      this.$v.localDate.$touch();
      if (this.$v.localDate.$error) return;
      this.$emit('update:expectedPaymentAt', this.localDate);
      this.editingDate = false;
    },
    cancelDate() {
      this.localDate = this.expectedPaymentAt;
      this.$v.localDate.$reset();
      this.editingDate = false;
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
$green-bg: #F2FAF5;
$green-border: #BFE3CD;
$green: #2FA36B;
$green-dark: #1F7A4E;
$amber-bg: #FFF8E6;
$amber-border: #F2C94C;
$amber-text: #8A6D1F;

.batch-live {
  background-color: $green-bg;
  border: $green-border solid $border-width;
  border-left: $green solid 4px;
  border-radius: $border-radius;
  position: sticky;
  top: $layout-spacing;
  &.is-empty {
    background-color: $gray-color-ultra-light;
    border: $border-color-dark dashed $border-width;
    .batch-live-empty {
      padding: $layout-spacing-lg;
      text-align: center;
      .batch-live-empty-icon {
        align-items: center;
        background-color: $light-color;
        border-radius: 50%;
        box-shadow: 0 2px 6px rgba($dark-color, .12);
        color: $primary-color;
        display: inline-flex;
        height: 2.2rem;
        justify-content: center;
        margin-bottom: $layout-spacing;
        width: 2.2rem;
      }
      .batch-live-empty-title {
        color: $gray-color-900;
        font-weight: 600;
        margin-bottom: $layout-spacing;
      }
      .batch-live-tips {
        color: $gray-color-dark;
        font-size: $font-size-xs;
        list-style: none;
        margin: 0;
        text-align: left;
        li {
          align-items: center;
          display: flex;
          gap: .3rem;
          margin: .15rem 0;
          svg {
            color: $green;
          }
        }
      }
    }
  }
  .batch-live-head {
    border-bottom: $green-border solid $border-width;
    padding: $layout-spacing $layout-spacing-lg;
    .batch-live-eyebrow {
      color: $green-dark;
      font-size: .5rem;
      font-weight: 700;
      letter-spacing: .08em;
      text-transform: uppercase;
    }
    .batch-live-title {
      color: $gray-color-900;
      font-size: $font-size-lg;
      font-weight: 700;
      margin: .05rem 0 .15rem;
    }
    .batch-live-count {
      align-items: center;
      color: $gray-color-dark;
      display: flex;
      font-size: $font-size-sm;
      justify-content: space-between;
      b {
        color: $gray-color-900;
      }
      .pill {
        border-color: $green-border;
        color: $green-dark;
      }
      .batch-live-total-sm {
        display: none;
      }
    }
    .batch-live-bar {
      background-color: $light-color;
      border: $green-border solid $border-width;
      border-radius: 1rem;
      height: .4rem;
      margin-top: .3rem;
      overflow: hidden;
      i {
        background-color: $green;
        display: block;
        height: 100%;
        transition: width .2s;
      }
      &.warn i {
        background-color: $warning-color;
      }
    }
  }
  .batch-live-sec {
    border-bottom: $green-border solid $border-width;
    padding: $layout-spacing $layout-spacing-lg;
    .batch-live-sec-title {
      color: $green-dark;
      font-size: $font-size-xs;
      font-weight: 700;
      letter-spacing: .06em;
      margin-bottom: .2rem;
      text-transform: uppercase;
    }
  }
  .batch-live-row {
    align-items: baseline;
    display: flex;
    font-size: $font-size-sm;
    gap: $layout-spacing;
    justify-content: space-between;
    padding: .1rem 0;
    .v {
      font-weight: 600;
    }
    &.big .v {
      color: $primary-color;
      font-size: 1rem;
      font-weight: 700;
    }
    .who {
      color: $gray-color-900;
      font-weight: 600;
      min-width: 0;
      small {
        color: $gray-color-dark;
        display: block;
        font-weight: 400;
      }
    }
  }
  .batch-live-more, .batch-live-edit {
    color: $secondary-color;
    font-size: $font-size-xs;
    &:focus {
      box-shadow: none;
    }
  }
  .batch-live-edit {
    font-weight: 400;
    margin-left: .3rem;
  }
  .batch-live-date {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-sm;
    margin-bottom: 0;
    .form-input {
      width: 5.5rem;
    }
    .form-input-hint {
      flex-basis: 100%;
    }
  }
  .batch-live-note {
    align-items: flex-start;
    background-color: $amber-bg;
    border: $amber-border solid $border-width;
    border-radius: $border-radius;
    color: $amber-text;
    display: flex;
    font-size: $font-size-xs;
    gap: .3rem;
    margin: $layout-spacing $layout-spacing-lg 0;
    padding: .3rem .4rem;
    svg {
      flex: 0 0 auto;
      margin-top: .1rem;
    }
    .batch-live-switch {
      color: $gray-color-900;
      font-size: $font-size-xs;
      margin: .3rem 0 0;
      padding-left: 1.6rem;
      .form-icon {
        top: .05rem;
      }
    }
    .batch-live-split {
      color: $gray-color-900;
      display: block;
      font-weight: 600;
      margin-top: .1rem;
    }
  }
  .batch-live-foot {
    padding: $layout-spacing $layout-spacing-lg $layout-spacing-lg;
    small {
      color: $gray-color-dark;
      display: block;
      margin-top: .3rem;
      text-align: center;
    }
    .btn-link {
      color: $gray-color-dark;
      margin-top: .2rem;
      &:focus {
        box-shadow: none;
      }
    }
    .btn-primary.tooltip::after {
      white-space: normal;
      width: 12rem;
    }
  }
  @media screen and (max-width: $size-md) {
    position: static;
    .batch-live-head {
      cursor: pointer;
      .batch-live-total-sm {
        display: inline;
      }
    }
    .batch-live-body {
      display: none;
    }
    &.is-open .batch-live-body {
      display: block;
    }
  }
}
</style>
